import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Divider,
  Chip,
} from "@mui/material";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import BusinessIcon from "@mui/icons-material/Business";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import AssessmentIcon from "@mui/icons-material/Assessment";
import LanguageIcon from "@mui/icons-material/Language";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForward";
import LaunchIcon from "@mui/icons-material/Launch";
import InfoBox from "../utils/InfoBox";
import { getImageUrl } from "../../../utils/helpers";

import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";

const scrollToSection = (id) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
  }
};

const KthPatentLicensing = ({ baseConfig, sectionStyles }) => {
  const {
    priColor,
    priHoverColor,
    tertColor,
    tertHoverColor,
    secColor,
    quadColor,
    quadHoverColor,
    linkColor,
  } = baseConfig.siteBranding;

  return (
    <Card sx={{ ...sectionStyles.card, mb: 2 }}>
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          backgroundColor: `${tertColor}22`,
        }}
      >
        <MonetizationOnIcon sx={{ mr: 2, color: tertColor }} />
        <Typography variant="h6" fontWeight="bold">
          Patent Licensing & Monetization
        </Typography>
      </Box>
      <CardContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="body1" paragraph>
              Patents represent valuable business assets that can generate
              significant revenue through strategic licensing and monetization.
              Our firm helps technology companies, investors, and research
              institutions develop and implement comprehensive strategies to
              maximize the return on their intellectual property investments.
            </Typography>

            <Typography variant="body1" paragraph>
              Whether you're seeking to license your patents to industry
              partners, negotiate royalty agreements, or prepare your portfolio
              for acquisition, our experienced team provides both legal and
              strategic guidance throughout the process. We understand how to
              position your patents to create maximum value in today's
              competitive technology marketplace.
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                gutterBottom
                color={tertColor}
              >
                Our Patent Licensing & Monetization Services:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Patent valuation and economic assessment"
                    secondary="Determine realistic market value for your intellectual property assets"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Licensing strategy development"
                    secondary="Create customized plans to monetize your patents through licensing"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Royalty negotiations and agreement drafting"
                    secondary="Secure optimal terms and conditions for your patent licenses"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Portfolio acquisition/sale guidance"
                    secondary="Navigate complex patent transactions with experienced legal support"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Technology transfer agreements"
                    secondary="Structure and document technology licensing arrangements"
                  />
                </ListItem>
              </List>
            </Box>

            <Box
              sx={{
                p: 2,
                mt: 2,
                backgroundColor: "#fff8e1",
                borderRadius: 2,
                border: "1px solid #ffe082",
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                gutterBottom
                color="#ff8f00"
              >
                FRAND Licensing for Standard Essential Patents (SEPs)
              </Typography>
              <Typography variant="body2">
                We provide specialized guidance for Standard Essential Patents
                (SEPs) subject to Fair, Reasonable, And Non-Discriminatory
                (FRAND) licensing obligations. Our expertise spans
                telecommunications, wireless technology, and electronics
                standards including IEEE, 3GPP, and other standard-setting
                organizations.
              </Typography>
              <Button
                component={RouterLink}
                to="/news-insights?category=sep"
                sx={{ mt: 1, color: "#ff8f00" }}
                endIcon={<LaunchIcon />}
              >
                Learn More About SEP Licensing
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <img
                src={getImageUrl("patent-monetization.png")}
                alt="Patent Licensing & Monetization"
                style={{
                  width: "100%",
                  maxWidth: "450px",
                  height: "auto",
                  borderRadius: "8px",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                }}
              />
            </Box>

            <Paper
              elevation={0}
              sx={{
                p: 3,
                backgroundColor: "#f5f5f5",
                borderRadius: 2,
                border: `1px solid ${tertColor}33`,
              }}
            >
              <Typography variant="h6" gutterBottom color={tertColor}>
                Maximizing Patent Value
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      backgroundColor: "white",
                      transition: "transform 0.2s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                      },
                    }}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={1}>
                        <BusinessIcon sx={{ color: tertColor, mr: 1 }} />
                        <Typography variant="subtitle2" fontWeight="bold">
                          Strategic Licensing
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        Target high-value licensees and structure agreements
                        that maximize both immediate and long-term revenue
                        potential.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      backgroundColor: "white",
                      transition: "transform 0.2s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                      },
                    }}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={1}>
                        <AccountBalanceIcon sx={{ color: tertColor, mr: 1 }} />
                        <Typography variant="subtitle2" fontWeight="bold">
                          Portfolio Development
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        Build valuable patent portfolios through strategic
                        filings, acquisitions, and licensing to maximize return
                        on investment.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      backgroundColor: "white",
                      transition: "transform 0.2s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                      },
                    }}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={1}>
                        <LanguageIcon sx={{ color: tertColor, mr: 1 }} />
                        <Typography variant="subtitle2" fontWeight="bold">
                          Cross-Industry Applications
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        Identify licensing opportunities in adjacent markets you
                        may not have considered, expanding potential revenue
                        streams.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      backgroundColor: "white",
                      transition: "transform 0.2s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                      },
                    }}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={1}>
                        <AssessmentIcon sx={{ color: tertColor, mr: 1 }} />
                        <Typography variant="subtitle2" fontWeight="bold">
                          Valuation Expertise
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        Determine accurate market valuations for patents to
                        support licensing negotiations, sales, or mergers and
                        acquisitions.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        {/* Patent Valuation & Licensing Agreements */}
        <Box sx={{ mt: 6 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              color: tertColor,
              borderBottom: `1px solid ${tertColor}33`,
              pb: 1,
            }}
          >
            Patent Valuation & Licensing Expertise
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: "100%",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <MonetizationOnIcon
                      sx={{ color: "#4caf50", mr: 1.5, fontSize: "2rem" }}
                    />
                    <Typography variant="h6" fontWeight="bold" color="#4caf50">
                      Patent Valuation
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body2" paragraph>
                    We employ sophisticated valuation methodologies to determine
                    the true worth of your patents, accounting for factors such
                    as:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <ChevronRightIcon sx={{ color: "#4caf50" }} />
                      </ListItemIcon>
                      <ListItemText primary="Market relevance and adoption potential" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ChevronRightIcon sx={{ color: "#4caf50" }} />
                      </ListItemIcon>
                      <ListItemText primary="Claim scope and enforceability" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ChevronRightIcon sx={{ color: "#4caf50" }} />
                      </ListItemIcon>
                      <ListItemText primary="Remaining patent term" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ChevronRightIcon sx={{ color: "#4caf50" }} />
                      </ListItemIcon>
                      <ListItemText primary="Industry licensing norms" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: "100%",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <VerifiedUserIcon
                      sx={{ color: "#2196f3", mr: 1.5, fontSize: "2rem" }}
                    />
                    <Typography variant="h6" fontWeight="bold" color="#2196f3">
                      License Agreement Drafting
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body2" paragraph>
                    We draft and negotiate tailored patent license agreements
                    that protect your interests while facilitating technology
                    transfer:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <ChevronRightIcon sx={{ color: "#2196f3" }} />
                      </ListItemIcon>
                      <ListItemText primary="Exclusive and non-exclusive licenses" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ChevronRightIcon sx={{ color: "#2196f3" }} />
                      </ListItemIcon>
                      <ListItemText primary="Field-of-use restrictions" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ChevronRightIcon sx={{ color: "#2196f3" }} />
                      </ListItemIcon>
                      <ListItemText primary="Performance milestones" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ChevronRightIcon sx={{ color: "#2196f3" }} />
                      </ListItemIcon>
                      <ListItemText primary="Royalty structures and auditing rights" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: "100%",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <BusinessIcon
                      sx={{ color: "#9c27b0", mr: 1.5, fontSize: "2rem" }}
                    />
                    <Typography variant="h6" fontWeight="bold" color="#9c27b0">
                      Technology Transfer
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body2" paragraph>
                    Comprehensive support for transferring technology and IP
                    assets between entities, whether through licensing, sale, or
                    strategic partnerships:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <ChevronRightIcon sx={{ color: "#9c27b0" }} />
                      </ListItemIcon>
                      <ListItemText primary="M&A IP due diligence" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ChevronRightIcon sx={{ color: "#9c27b0" }} />
                      </ListItemIcon>
                      <ListItemText primary="Joint development agreements" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ChevronRightIcon sx={{ color: "#9c27b0" }} />
                      </ListItemIcon>
                      <ListItemText primary="Research collaboration frameworks" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ChevronRightIcon sx={{ color: "#9c27b0" }} />
                      </ListItemIcon>
                      <ListItemText primary="IP assignment agreements" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Related Intellectual Property Services */}
        <Box sx={{ mt: 6 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              color: tertColor,
              borderBottom: `1px solid ${tertColor}33`,
              pb: 1,
            }}
          >
            Related Intellectual Property Services
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                component={RouterLink}
                to="/trademarks"
                sx={{
                  p: 2,
                  mt: 2,

                  height: "100%",
                  borderColor: tertColor,
                  color: tertColor,
                  display: "block",
                  textAlign: "left",
                  "&:hover": {
                    borderColor: tertHoverColor,
                    backgroundColor: `${tertColor}15`,
                  },
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Trademark Services
                </Typography>
                <Typography variant="body2" color={priColor}>
                  Brand protection strategies and trademark registration
                </Typography>
              </Button>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                component={RouterLink}
                to="/software-licensing"
                sx={{
                  p: 2,
                  mt: 2,

                  height: "100%",
                  borderColor: tertColor,
                  color: tertColor,
                  display: "block",
                  textAlign: "left",
                  "&:hover": {
                    borderColor: tertHoverColor,
                    backgroundColor: `${tertColor}15`,
                  },
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Software Licensing
                </Typography>
                <Typography variant="body2" color={priColor}>
                  Licensing strategies for software and digital assets
                </Typography>
              </Button>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                component={RouterLink}
                to="/trade-secrets"
                sx={{
                  p: 2,
                  mt: 2,

                  height: "100%",
                  borderColor: tertColor,
                  color: tertColor,
                  display: "block",
                  textAlign: "left",
                  "&:hover": {
                    borderColor: tertHoverColor,
                    backgroundColor: `${tertColor}15`,
                  },
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Trade Secret Protection
                </Typography>
                <Typography variant="body2" color={priColor}>
                  Strategies for protecting valuable confidential information
                </Typography>
              </Button>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                component={RouterLink}
                to="/patent-ai-resources"
                sx={{
                  p: 2,
                  mt: 2,

                  height: "100%",
                  borderColor: tertColor,
                  color: tertColor,
                  display: "block",
                  textAlign: "left",
                  "&:hover": {
                    borderColor: tertHoverColor,
                    backgroundColor: `${tertColor}15`,
                  },
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Patent AI Resources
                </Typography>
                <Typography variant="body2" color={priColor}>
                  AI tools and resources for patent analysis and management
                </Typography>
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Patent Monetization Case Study */}
        <Box sx={{ mt: 6 }}>
          <InfoBox
            title="Success Story: Technology Patent Licensing"
            content={
              <Typography variant="body2">
                We helped a technology startup develop and implement a licensing
                strategy for their patent portfolio. After conducting a
                comprehensive valuation analysis and identifying target
                licensees, we negotiated favorable licensing terms with multiple
                industry players. The resulting revenue stream provided
                consistent funding for continued R&D efforts and significantly
                increased the company's valuation during their next funding
                round.
              </Typography>
            }
            type="success"
          />
        </Box>
        <Button
          startIcon={<PhoneInTalkIcon />}
          variant="contained"
          onClick={() => scrollToSection("contact-section")}
          sx={{
            backgroundColor: tertColor,
            mt: 2,
            mb: 4,
            color: "white",
            "&:hover": {
              color: "white",
              backgroundColor: tertHoverColor,
            },
          }}
        >
          Schedule a Consultation
        </Button>
      </CardContent>
    </Card>
  );
};

export default KthPatentLicensing;
